import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { 
  Brain,
  Info,
  AlertTriangle,
  CheckCircle2,
  Activity,
  Moon,
  Eye,
  RotateCcw,
  Sparkles
} from 'lucide-react';

export default function RamsayScorePage() {
  const [selectedLevel, setSelectedLevel] = useState(null);

  const ramsayLevels = [
    {
      level: 1,
      title: "Patient anxieux, agité",
      description: "Le patient présente des signes d'anxiété et d'agitation. Il est potentiellement difficile à gérer et pourrait exprimer son inconfort.",
      interpretation: "État de vigilance excessive avec agitation",
      action: "Nécessite une sédation ou une anxiolyse",
      color: "bg-red-500/10 border-red-500/30",
      badgeColor: "bg-red-500",
      icon: AlertTriangle,
      iconColor: "text-red-400",
      category: "Éveillé - Agité"
    },
    {
      level: 2,
      title: "Patient coopérant, orienté et calme",
      description: "Le patient est considéré comme étant dans un état calme, coopératif et orienté. Il est réceptif aux interactions et aux instructions.",
      interpretation: "État de vigilance normal",
      action: "État optimal, aucune intervention nécessaire",
      color: "bg-emerald-500/10 border-emerald-500/30",
      badgeColor: "bg-emerald-500",
      icon: CheckCircle2,
      iconColor: "text-emerald-400",
      category: "Éveillé - Coopérant"
    },
    {
      level: 3,
      title: "Patient répondant aux ordres",
      description: "Le patient réagit aux ordres verbaux ou aux commandes simples. Il peut exécuter des actions demandées, bien qu'il puisse être endormi ou moins alerte.",
      interpretation: "Sédation légère - Niveau cible pour sédation procédurale",
      action: "Niveau optimal pour procédures courtes",
      color: "bg-yellow-500/10 border-yellow-500/30",
      badgeColor: "bg-yellow-500",
      icon: Eye,
      iconColor: "text-yellow-400",
      category: "Sédation Légère",
      highlighted: true
    },
    {
      level: 4,
      title: "Patient endormi avec réponse nette à la stimulation",
      description: "Le patient est endormi mais présente une réponse claire et identifiable lorsqu'une stimulation (tactile ou auditive) lui est appliquée.",
      interpretation: "Sédation modérée",
      action: "Niveau acceptable pour sédation procédurale",
      color: "bg-cyan-500/10 border-cyan-500/30",
      badgeColor: "bg-cyan-500",
      icon: Activity,
      iconColor: "text-cyan-400",
      category: "Sédation Modérée"
    },
    {
      level: 5,
      title: "Patient endormi répondant faiblement aux stimulations",
      description: "Le patient est profondément endormi et ne réagit que de manière faible ou limitée aux stimulations. La réponse est moins évidente que dans le niveau 4.",
      interpretation: "Sédation profonde",
      action: "Surveillance respiratoire renforcée nécessaire",
      color: "bg-amber-500/10 border-amber-500/30",
      badgeColor: "bg-amber-500",
      icon: Moon,
      iconColor: "text-amber-400",
      category: "Sédation Profonde"
    },
    {
      level: 6,
      title: "Pas de réponse aux stimulations nociceptives",
      description: "Le patient est dans un état d'inconscience profonde et ne manifeste aucune réponse, même lorsqu'il est soumis à des stimulations douloureuses (nociceptives).",
      interpretation: "Sédation très profonde / Anesthésie",
      action: "Surveillance intensive obligatoire - Risque élevé",
      color: "bg-red-500/10 border-red-500/30",
      badgeColor: "bg-red-500",
      icon: Brain,
      iconColor: "text-red-400",
      category: "Anesthésie"
    }
  ];

  const handleReset = () => {
    setSelectedLevel(null);
  };

  const getInterpretationSummary = () => {
    if (!selectedLevel) return null;
    
    const level = ramsayLevels.find(l => l.level === selectedLevel);
    
    let clinicalContext = '';
    let recommendations = [];
    
    if (selectedLevel === 1) {
      clinicalContext = "Le patient est en état d'agitation, ce qui peut compromettre sa sécurité et celle de l'équipe soignante.";
      recommendations = [
        "Évaluer la cause de l'agitation (douleur, hypoxie, delirium)",
        "Considérer une anxiolyse (benzodiazépines) ou sédation légère",
        "Assurer la sécurité du patient et de l'environnement",
        "Réévaluer fréquemment"
      ];
    } else if (selectedLevel === 2) {
      clinicalContext = "Le patient est dans un état optimal de vigilance et de coopération.";
      recommendations = [
        "Aucune intervention sur la sédation nécessaire",
        "État idéal pour interaction et évaluation",
        "Maintenir les conditions actuelles"
      ];
    } else if (selectedLevel === 3) {
      clinicalContext = "Le patient est légèrement sédaté mais réactif aux ordres. C'est le niveau cible pour la plupart des sédations procédurales (Ramsay 4-5 visé).";
      recommendations = [
        "Niveau approprié pour procédures courtes",
        "Patient capable de protéger ses voies aériennes",
        "Surveillance standard requise",
        "Peut nécessiter une augmentation pour procédures douloureuses"
      ];
    } else if (selectedLevel === 4) {
      clinicalContext = "Sédation modérée appropriée pour de nombreuses procédures. Le patient répond aux stimulations mais reste calme.";
      recommendations = [
        "Niveau cible pour sédation procédurale",
        "Monitorage : SpO2, ECG, PA/3-5 min",
        "Patient peut nécessiter une aide pour maintenir les voies aériennes",
        "Surveillance continue obligatoire"
      ];
    } else if (selectedLevel === 5) {
      clinicalContext = "Sédation profonde. Le patient est proche de l'anesthésie générale avec risque accru de dépression respiratoire.";
      recommendations = [
        "Surveillance rapprochée OBLIGATOIRE",
        "Monitorage : SpO2, ETCO2, ECG, PA continue",
        "Matériel de réanimation prêt (BAVU, aspiration, IOT)",
        "Praticien formé à la gestion des voies aériennes",
        "Considérer une réduction de la sédation si non nécessaire"
      ];
    } else if (selectedLevel === 6) {
      clinicalContext = "Sédation très profonde ou anesthésie générale. Risque maximal de complications respiratoires et hémodynamiques.";
      recommendations = [
        "🚨 SURVEILLANCE INTENSIVE OBLIGATOIRE",
        "Monitorage complet : SpO2, ETCO2, ECG, PA invasive si possible",
        "Gestion avancée des voies aériennes souvent nécessaire",
        "Équipe d'anesthésie/réanimation présente",
        "Évaluer la nécessité de ce niveau de sédation",
        "Préparer antagonistes si surdosage médicamenteux"
      ];
    }
    
    return {
      clinicalContext,
      recommendations,
      level
    };
  };

  const summary = getInterpretationSummary();

  return (
    <div className="min-h-screen bg-[hsl(var(--bg))] text-[hsl(var(--text))] p-4 md:p-6" data-testid="ramsay-score-page">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-3">
          <div className="flex items-center justify-center gap-3">
            <Brain className="w-10 h-10 text-cyan-400" />
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-cyan-300 bg-clip-text text-transparent">
              Score de Ramsay
            </h1>
          </div>
          <p className="text-[hsl(var(--text-dim))] text-sm md:text-base max-w-3xl mx-auto">
            Échelle d'évaluation du niveau de sédation - Outil de référence en soins intensifs et sédation procédurale
          </p>
        </div>

        {/* Info Card */}
        <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-cyan-300">
              <Info className="w-5 h-5" />
              À propos du Score de Ramsay
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-[hsl(var(--text-dim))]">
            <p>
              Le <strong className="text-[hsl(var(--text))]">Score de Ramsay</strong> est une échelle clinique permettant d'évaluer objectivement le niveau de sédation ou d'agitation d'un patient.
            </p>
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div className="p-3 rounded-lg bg-cyan-500/5 border border-cyan-500/20">
                <h4 className="font-semibold text-cyan-300 mb-2">Utilisation en Sédation Procédurale</h4>
                <p className="text-xs">
                  Niveaux cibles : <strong>Ramsay 3-5</strong> selon la procédure. Le niveau 4-5 est optimal pour la plupart des procédures douloureuses.
                </p>
              </div>
              <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
                <h4 className="font-semibold text-amber-300 mb-2">Utilisation en Réanimation</h4>
                <p className="text-xs">
                  Permet d'ajuster la sédation des patients ventilés mécaniquement pour assurer confort et tolérance au respirateur.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Score Selection */}
        <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-cyan-300">
                <Activity className="w-5 h-5" />
                Sélectionnez le Niveau de Ramsay
              </CardTitle>
              {selectedLevel && (
                <Button
                  onClick={handleReset}
                  variant="ghost"
                  size="sm"
                  className="text-cyan-400 hover:text-cyan-300"
                  data-testid="reset-button"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Réinitialiser
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              {ramsayLevels.map((item) => {
                const Icon = item.icon;
                const isSelected = selectedLevel === item.level;
                
                return (
                  <button
                    key={item.level}
                    onClick={() => setSelectedLevel(item.level)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      isSelected
                        ? `${item.color} border-current shadow-lg`
                        : 'bg-[hsl(var(--bg))] border-[hsl(var(--border))] hover:border-cyan-500/30'
                    } ${item.highlighted ? 'ring-2 ring-yellow-500/50' : ''}`}
                    data-testid={`ramsay-level-${item.level}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`flex-shrink-0 w-12 h-12 rounded-full ${item.color} flex items-center justify-center`}>
                        <Icon className={`w-6 h-6 ${item.iconColor}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={`${item.badgeColor} text-white font-bold`}>
                            Niveau {item.level}
                          </Badge>
                          <span className="text-xs text-[hsl(var(--text-dim))]">{item.category}</span>
                          {item.highlighted && (
                            <Badge variant="outline" className="border-yellow-500/50 text-yellow-400 text-xs">
                              <Sparkles className="w-3 h-3 mr-1" />
                              Cible sédation
                            </Badge>
                          )}
                        </div>
                        <h3 className="font-semibold text-[hsl(var(--text))] mb-1">{item.title}</h3>
                        <p className="text-sm text-[hsl(var(--text-dim))] mb-2">{item.description}</p>
                        <div className="flex items-start gap-2 text-xs">
                          <Badge variant="outline" className="border-cyan-500/30 text-cyan-300">
                            {item.interpretation}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Interpretation */}
        {summary && (
          <Card className={`${summary.level.color} border-2`} data-testid="interpretation-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[hsl(var(--text))]">
                <Brain className={`w-5 h-5 ${summary.level.iconColor}`} />
                Interprétation Clinique - Ramsay {summary.level.level}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Contexte clinique */}
              <Alert className={`${summary.level.color} border-none`}>
                <Info className={`w-4 h-4 ${summary.level.iconColor}`} />
                <AlertDescription className="text-[hsl(var(--text))]">
                  {summary.clinicalContext}
                </AlertDescription>
              </Alert>

              {/* Actions recommandées */}
              <div>
                <h4 className="font-semibold text-[hsl(var(--text))] mb-3 flex items-center gap-2">
                  <CheckCircle2 className={`w-4 h-4 ${summary.level.iconColor}`} />
                  Actions Recommandées :
                </h4>
                <ul className="space-y-2">
                  {summary.recommendations.map((rec, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-[hsl(var(--text-dim))]">
                      <span className={`mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full ${summary.level.badgeColor}`} />
                      <span>{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Action clinique */}
              <div className="p-3 rounded-lg bg-[hsl(var(--bg))] border border-[hsl(var(--border))]">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold text-[hsl(var(--text))] mb-1">Action Immédiate :</p>
                    <p className="text-xs text-[hsl(var(--text-dim))]">{summary.level.action}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Référence */}
        <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]">
          <CardContent className="pt-6">
            <div className="text-xs text-[hsl(var(--text-dim))] space-y-2">
              <p>
                <strong className="text-[hsl(var(--text))]">Référence :</strong> Ramsay MA, Savege TM, Simpson BR, Goodwin R. 
                Controlled sedation with alphaxalone-alphadolone. Br Med J. 1974;2(5920):656-659.
              </p>
              <p>
                <strong className="text-[hsl(var(--text))]">Note :</strong> Le Score de Ramsay reste un outil subjectif. 
                Pour une évaluation plus objective en réanimation, considérer également RASS (Richmond Agitation-Sedation Scale) ou BIS (Bispectral Index).
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
